import { useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';

import SaveOutlined from '@ant-design/icons/SaveOutlined';
import { notification, Tooltip, Alert, Row, Col, Switch, Input, Select, Modal } from 'antd';

import { isUserElite } from 'helpers/elite';
import { isEmpty } from 'helpers/lodash';
import { IEventForm } from 'interfaces/event';

import FormContext from '../../../Context';
import Actions from '../Actions';
import styles from './styles.module.css';

const FormFour = () => {
  const [openModalMarketplace, setOpenModalMarketplace] = useState(false);
  const { handleEdit, isCreate, loadingEdit, jobsLotPending, setCurrentStep, setStepsEnables, stepsEnables, user } =
    useContext(FormContext);

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    control,
    getFieldState,
    clearErrors
  } = useFormContext<IEventForm>();

  const isElite = isUserElite(user);
  const hasReserveTime = useWatch({ control, name: 'hasReserveTime' });
  const reserveTime = useWatch({ control, name: 'reserve_time' });
  const dataCaptureActive = useWatch({ control, name: 'active_data_capture' });
  const dataCaptureType = useWatch({ control, name: 'data_capture_type' });
  const salespageActive = useWatch({ control, name: 'salespage_active' });

  useEffect(() => {
    const isDirty = getFieldState('marketplace_active').isDirty;

    if (isElite && !isDirty && isCreate) {
      setValue('marketplace_active', true);
    }
  }, [getFieldState, isCreate, isElite, setValue]);

  const handleNext = useCallback(() => {
    const enablesSteps = Array.from(stepsEnables);

    if (!enablesSteps.includes('5')) {
      enablesSteps.push('5');
      setStepsEnables(enablesSteps);
    }

    setCurrentStep('5');
  }, [stepsEnables, setCurrentStep, setStepsEnables]);

  const handlePrimaryButtonAction = () => {
    if (!isEmpty(errors)) {
      notification.error({ message: 'Verifique todos os campos' });
      return;
    }

    handleSubmit(isCreate ? handleNext : handleEdit)();
  };

  const handleActiveDataCaptureChange = (isActive: boolean) => {
    if (!isActive) {
      setValue('active_precheckout', false);
      setValue('active_postcheckout', false);
      setValue('data_capture_type', null);
      clearErrors('data_capture_type');
    }

    setValue('active_data_capture', isActive);
  };

  const handleDataCaptureTypeChange = (selectedType: string) => {
    setValue('data_capture_type', selectedType);

    if (selectedType === 'post_checkout') {
      setValue('active_precheckout', false);
      setValue('active_postcheckout', true);
      setValue('hasReserveTime', true);
    }

    if (selectedType === 'post_checkout' && reserveTime === 0) {
      setValue('reserve_time', 15);
    }

    if (selectedType === 'pre_checkout') {
      setValue('hasReserveTime', false);
      setValue('reserve_time', 15);
      setValue('active_postcheckout', false);
      setValue('active_precheckout', true);
    }
  };

  const handleMarketplaceChange = (isActive: boolean) => {
    if (!isCreate && !salespageActive && isActive) {
      setOpenModalMarketplace(true);
      return;
    }

    if (isCreate && !salespageActive) {
      setValue('salespage_active', true);
      setValue('marketplace_active', isActive);
      return;
    }

    setValue('marketplace_active', isActive);
  };

  const handleModalMarketplaceConfirm = () => {
    setOpenModalMarketplace(false);
    setValue('marketplace_active', true);
    setValue('salespage_active', true);
  };

  const handleChangeHasReserveTime = (isActive: boolean) => {
    if (!isActive) {
      clearErrors('reserve_time');
    }

    setValue('hasReserveTime', isActive);
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <h6 className={styles.title}>Configurações</h6>
        <h6 className={styles.subtitle}>Controle avançado da experiência de compra e da exibição do seu evento.</h6>
      </Col>

      <div className={styles.container}>
        <Col span={24}>
          <h6 className={isElite ? styles.title : `${styles.disabled} ${styles.title}`}>
            Exibir na vitrine de eventos
          </h6>
          {isElite ? (
            <h6 className={styles.subtitle}>
              Habilite para exibir seu evento na{' '}
              <a
                href='https://ajuda.eduzz.com/hc/pt-br/articles/36826196732059-Como-funciona-o-Marketplace-de-Eventos-Blinket'
                style={{ color: '#0F235C' }}
                target='_blank'
                rel='noreferrer'
              >
                Vitrine de Eventos da Blinket
              </a>
            </h6>
          ) : (
            <h6 className={`${styles.disabled} ${styles.subtitle}`}>
              Essa funcionalidade está disponível apenas para produtores a partir da faixa Red Belt no Programa Elite.
              Suba de faixa e conquiste seu espaço.{' '}
              <a href='https://programaelite.eduzz.com/' style={{ color: '#0F235C' }} target='_blank' rel='noreferrer'>
                Saiba mais.
              </a>
            </h6>
          )}
        </Col>

        <Col span={24}>
          <Controller
            control={control}
            name='marketplace_active'
            render={({ field: { value } }) => (
              <span className={styles.formAction}>
                <Switch
                  id='switch-data-capture'
                  defaultValue={true}
                  checked={value ?? false}
                  disabled={!isElite}
                  onChange={handleMarketplaceChange}
                />
                <p className={isElite ? '' : `${styles.disabled}`}>Exibir na Vitrine de Eventos</p>
              </span>
            )}
          />
        </Col>
      </div>

      <div className={styles.container}>
        <Col span={24}>
          <h6 className={styles.title}>Coletar dados dos participantes</h6>
          <h6 className={styles.subtitle}>
            Habilite para capturar os dados dos participantes (Nome, e-mail, CPF e telefone) no momento da compra.
          </h6>
        </Col>

        <Controller
          control={control}
          name='active_data_capture'
          render={({ field: { value } }) => (
            <span className={styles.formAction}>
              <Switch id='switch-data-capture' value={value ?? false} onChange={handleActiveDataCaptureChange} />
              <p>{`Captação de dados está ${value ? 'ativada' : 'desativada'}`}</p>
            </span>
          )}
        />

        <Controller
          control={control}
          name='data_capture_type'
          render={({ field: { value }, fieldState: { error } }) => (
            <div className={styles.dataCapture}>
              <p>Selecione o momento da captação dos dados</p>
              <Select
                id='data-capture-select'
                size='large'
                placeholder='Selecione o momento da captação dos dados'
                disabled={!dataCaptureActive}
                value={value}
                options={[
                  { value: 'pre_checkout', label: 'Antes do Pagamento (Pré-Checkout)' },
                  { value: 'post_checkout', label: 'Depois do Pagamento (Página de Obrigado)' }
                ]}
                onChange={handleDataCaptureTypeChange}
                status={error ? 'error' : ''}
              />
              <p className={styles.errors}>{error?.message}</p>
            </div>
          )}
        />
      </div>

      <div className={styles.container}>
        <Col span={24}>
          <h6 className={styles.title}>Tempo limite de reserva de ingressos</h6>
          <h6 className={styles.subtitle}>
            Ao acessar o checkout, um tempo limite é ativado para reservar o ingresso enquanto você finaliza o
            pagamento. Isso garante que o ingresso não seja comprado por outra pessoa durante o processo.
          </h6>
        </Col>

        <Col span={24}>
          <div className={styles.hasReserveTime}>
            <Controller
              control={control}
              name='hasReserveTime'
              render={({ field: { value } }) => (
                <span className={styles.formAction}>
                  <Switch
                    id='check-reserve-time'
                    value={value ?? true}
                    onChange={handleChangeHasReserveTime}
                    disabled={dataCaptureType === 'pre_checkout'}
                  />
                  <p>{`Tempo de reserva de ingressos está ${value ? 'ativado' : 'desativado'}`}</p>
                </span>
              )}
            />

            <Alert
              type='warning'
              message='Não ative esta opção para vendas de lançamentos. O tempo limite padrão de reserva é de 15 minutos.'
              showIcon
            />
          </div>

          <div className={styles.reserveTime}>
            <p>Tempo para reserva</p>
            <Controller
              control={control}
              name='reserve_time'
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Input
                    suffix={<p>{reserveTime === 1 ? 'Minuto' : 'Minutos'}</p>}
                    type='number'
                    size='large'
                    id='reserve_time-form-one'
                    value={value ?? 15}
                    onChange={onChange}
                    disabled={!hasReserveTime}
                    status={error ? 'error' : ''}
                  />
                  <p className={styles.errors}>{error?.message}</p>
                </>
              )}
            />
          </div>
        </Col>
      </div>

      <Col span={24}>
        <Actions>
          {isCreate && (
            <Actions.Button
              type='text'
              size='large'
              id='stepback-btn-form-4'
              onClick={() => {
                if (!isEmpty(window['formErrors'])) return;
                setCurrentStep('3');
              }}
            >
              Voltar
            </Actions.Button>
          )}
          <Tooltip defaultOpen={!!jobsLotPending} open={!!jobsLotPending} title={jobsLotPending} color='#ffbc00'>
            <Actions.Button
              onClick={handlePrimaryButtonAction}
              size='large'
              id={isCreate ? 'step-forward-btn-form-4' : 'save-btn-form-4'}
              type='primary'
              icon={!isCreate && <SaveOutlined />}
              disabled={loadingEdit || !!jobsLotPending}
            >
              {isCreate ? 'Próximo' : 'Salvar'}
            </Actions.Button>
          </Tooltip>
        </Actions>
      </Col>

      <Modal
        title='Atenção'
        open={openModalMarketplace}
        onOk={handleModalMarketplaceConfirm}
        onCancel={() => setOpenModalMarketplace(false)}
        okText='Continuar'
        cancelText='Cancelar'
      >
        <p>
          Sua página de vendas está desativada. Ao prosseguir, sua página de vendas será ativada novamente. Deseja
          continuar?
        </p>
      </Modal>
    </Row>
  );
};
export default FormFour;
